import type { ComponentPropsWithoutRef } from 'react';
import { Button } from '@newcore/ui/buttons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Skeleton } from '@newcore/ui/skeleton';
import { Text } from '@newcore/ui/typography';

export interface FilterTabOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

type FilterTabsElementProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'onChange' | 'role'
>;

type FilterTabsProps<T extends string> = FilterTabsElementProps & {
  options: FilterTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  areCountsLoading?: boolean;
  'aria-label': string;
};

export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
  areCountsLoading = false,
  className,
  'aria-label': ariaLabel,
  ...rest
}: FilterTabsProps<T>) {
  return (
    <div
      {...rest}
      role='tablist'
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1', className)}
    >
      {options.map(option => {
        const isActive = option.value === value;
        const hasCount = option.count !== undefined;
        const shouldRenderCount = hasCount || areCountsLoading;
        const countReserve = hasCount ? String(option.count) : '99';

        return (
          <Button
            key={option.value}
            type='button'
            role='tab'
            aria-selected={isActive}
            variant={ButtonVariants.TERTIARY}
            size={Sizes.SM}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-auto gap-1.5 rounded-lg px-2.5',
              shouldRenderCount ? 'py-1' : 'py-1.5',
              isActive
                ? 'bg-tertiary text-primary hover:not-disabled:bg-tertiary hover:not-disabled:text-primary'
                : 'text-quaternary hover:not-disabled:bg-tertiary/60 hover:not-disabled:text-primary'
            )}
          >
            <Text size={TextSizes.MD} weight={FontWeights.SEMIBOLD}>
              {option.label}
            </Text>
            {shouldRenderCount &&
              (areCountsLoading ? (
                <span
                  className='inline-grid shrink-0 tabular-nums'
                  data-testid='filter-tab-count-loading'
                >
                  <Text
                    size={TextSizes.MD}
                    weight={FontWeights.MEDIUM}
                    className='invisible col-start-1 row-start-1 select-none'
                    aria-hidden
                  >
                    {countReserve}
                  </Text>
                  <Skeleton
                    className='col-start-1 row-start-1 size-[0.65em] shrink-0 self-center rounded-sm !bg-quaternary'
                    aria-hidden
                  />
                </span>
              ) : (
                <Text
                  size={TextSizes.MD}
                  weight={FontWeights.MEDIUM}
                  className='text-quaternary'
                >
                  {option.count}
                </Text>
              ))}
          </Button>
        );
      })}
    </div>
  );
}
